import { ExternalLink, Table as TableIcon, X } from 'lucide-react'
import ReactMarkdown, { type Components } from 'react-markdown'
import type { Evidence, ProofPassageEntry } from '@/types/api'

interface SurgerySourceRowProps {
  evidence: Evidence
  sourceLabel: string
  isExpanded: boolean
  hiddenPassageIds: Set<string>
  onToggle: () => void
  /** Header "View in PDF" — opens the PDF with every visible passage highlighted
   *  at once. Useful for a holistic skim. Per-passage rows below have their own
   *  jump-to-passage actions. */
  onViewSource: () => void
  /** Source-level remove (drops every passage). Falls back to the same flow
   *  used today; the writer can also remove individual passages. */
  onRemoveSource: () => void
  /** Per-passage actions. */
  onViewPassage: (passage: ProofPassageEntry) => void
  onRemovePassage: (passage: ProofPassageEntry) => void
  /** Read-only mode (e.g. the public share view): disables both "View in PDF"
   *  buttons with a tooltip and hides every destructive control. */
  readOnly?: boolean
  /** Hover tooltip shown on the disabled "View in PDF" buttons when
   *  ``readOnly`` is true. Defaults to "Not available in shared view". */
  readOnlyPdfTooltip?: string
}

const mdComponents: Components = {
  p: ({ children }) => <>{children}</>,
  em: ({ children }) => <i>{children}</i>,
}

const supportTagStyle: Record<string, string> = {
  supports:           'text-[var(--status-verified-text)]',
  partially_supports: 'text-[var(--status-partial-text)]',
  contradicts:        'text-[var(--status-error-text)]',
  not_relevant:       'text-muted-foreground',
}

const supportTagLabel: Record<string, string> = {
  supports:           'Supports',
  partially_supports: 'Partially supports',
  contradicts:        'Contradicts',
  not_relevant:       'Not relevant',
}

function passagePageLabel(p: ProofPassageEntry): string {
  const pages = Array.from(new Set(p.boxes.map((b) => b.page_number))).sort((a, b) => a - b)
  if (pages.length === 0) return ''
  if (pages.length === 1) return `p.${pages[0]}`
  return `pp.${pages.join(', ')}`
}

export function SurgerySourceRow({
  evidence,
  sourceLabel,
  isExpanded,
  hiddenPassageIds,
  onToggle,
  onViewSource,
  onRemoveSource,
  onViewPassage,
  onRemovePassage,
  readOnly = false,
  readOnlyPdfTooltip = 'Not available in shared view',
}: SurgerySourceRowProps) {
  const isContra = evidence.support_level === 'contradicts'
  const tagColor = supportTagStyle[evidence.support_level] ?? supportTagStyle.supports
  const tagLabel = supportTagLabel[evidence.support_level] ?? 'Supports'
  const passagesWithText = evidence.proof_passages.filter((p) => p.passage_text)
  const visiblePassages = passagesWithText.filter((p) => !hiddenPassageIds.has(p.passage_id))
  const previewText = visiblePassages[0]?.passage_text ?? ''

  return (
    <div
      className={`bg-muted rounded-lg mb-1.5 overflow-hidden transition-all duration-200 ${
        isContra ? 'border-l-2 border-l-[var(--status-error-text)]' : ''
      }`}
    >
      {/* Header — always visible, clickable */}
      <div
        className="flex items-start gap-1.5 px-2.5 py-2 cursor-pointer hover:bg-black/[0.04]"
        onClick={onToggle}
      >
        <div className="flex-1 min-w-0">
          <div className={`text-[9px] font-semibold uppercase tracking-wide ${tagColor}`}>
            {tagLabel}
          </div>
          <div className="text-[11px] font-medium text-foreground leading-tight">
            <ReactMarkdown components={mdComponents}>{sourceLabel}</ReactMarkdown>
          </div>
          {!isExpanded && previewText && (
            <div className="text-[10px] text-muted-foreground truncate mt-0.5 leading-tight">
              "{previewText}"
              {visiblePassages.length > 1 && (
                <span className="text-muted-foreground/70"> (+{visiblePassages.length - 1} more)</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {readOnly ? (
            <span
              className="w-6 h-6 flex items-center justify-center rounded text-muted-foreground/60 cursor-not-allowed"
              onClick={e => e.stopPropagation()}
              title={readOnlyPdfTooltip}
            >
              <ExternalLink className="h-3 w-3" />
            </span>
          ) : (
            <button
              type="button"
              className="w-6 h-6 flex items-center justify-center rounded text-muted-foreground hover:bg-card"
              onClick={e => { e.stopPropagation(); onViewSource() }}
              title={visiblePassages.length > 1 ? 'View all passages in PDF' : 'View in PDF'}
            >
              <ExternalLink className="h-3 w-3" />
            </button>
          )}
          <span
            className={`w-6 h-6 flex items-center justify-center text-[10px] text-muted-foreground transition-transform duration-200 ${
              isExpanded ? 'rotate-90' : ''
            }`}
          >
            ›
          </span>
        </div>
      </div>

      {/* Detail — only when expanded */}
      {isExpanded && (
        <div className="px-2.5 pb-2.5">
          {visiblePassages.map((p, i) => {
            const pageLabel = passagePageLabel(p)
            const isTable = p.kind === 'table'
            // Caption + matched-cell count as the hover tooltip; useful when the
            // synthesized subtable text doesn't show the caption inline (the
            // ≥90%-cells case falls back to caption-only, but the <90% case
            // renders the cells and hides the caption).
            const tableChipTitle = isTable && p.table
              ? `${p.table.caption ?? 'Table'} · ${p.table.matched_cells_count} cell${p.table.matched_cells_count === 1 ? '' : 's'}${p.table.table_cell_total ? ` of ${p.table.table_cell_total}` : ''}`
              : undefined
            return (
              <div
                key={p.passage_id || i}
                className="bg-card rounded-md border border-border/40 mb-2 overflow-hidden"
              >
                {/* whitespace-pre-line preserves newlines in synthesized
                    subtable text (one line per row, cells joined by ·).
                    Prose passages have no newlines so the class is a no-op. */}
                <div className="text-[11px] text-foreground leading-relaxed p-2 whitespace-pre-line">
                  "{p.passage_text}"
                </div>
                <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-t border-border/40 bg-muted/30">
                  <div className="flex items-center gap-2 min-w-0">
                    {pageLabel ? (
                      <span className="text-[10px] text-muted-foreground">{pageLabel}</span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/70 italic">no PDF coordinates</span>
                    )}
                    {isTable && (
                      <span
                        title={tableChipTitle}
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border/40"
                      >
                        <TableIcon className="h-2.5 w-2.5" />
                        Table
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="text-[10px] px-2 py-0.5 rounded text-primary hover:bg-primary/10 inline-flex items-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => { if (!readOnly) onViewPassage(p) }}
                      disabled={readOnly || p.boxes.length === 0}
                      title={readOnly
                        ? readOnlyPdfTooltip
                        : (p.boxes.length === 0
                          ? 'No PDF coordinates available for this passage'
                          : 'View this passage in PDF')}
                    >
                      <ExternalLink className="h-2.5 w-2.5" />
                      View in PDF
                    </button>
                    {!readOnly && (
                      <button
                        type="button"
                        className="text-[10px] w-5 h-5 inline-flex items-center justify-center rounded text-muted-foreground hover:bg-[var(--status-error-bg)] hover:text-[var(--status-error-text)] transition-colors"
                        onClick={() => onRemovePassage(p)}
                        title="Remove this passage"
                        aria-label="Remove this passage"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
          {evidence.reasoning && (
            <div className="text-[10px] text-muted-foreground leading-relaxed p-1.5 px-2 bg-card rounded-md border-l-2 border-l-border mb-2">
              <span className="font-medium text-muted-foreground">AI reasoning: </span>
              {evidence.reasoning}
            </div>
          )}
          {!readOnly && (
            <div className="flex gap-1.5">
              <button
                type="button"
                className="text-[10px] px-2.5 py-1 rounded-md text-[var(--status-error-text)] border border-border/40 hover:bg-[var(--status-error-bg)] inline-flex items-center gap-1 transition-colors"
                onClick={onRemoveSource}
              >
                Remove source
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
